import React, {useEffect, useMemo, useState} from 'react';
import {debounce} from 'lodash';

import {TextArea} from '@albert-io/atomic';

const NOTE_SAVE_DEBOUNCE_MS = 300;

interface AnnotationNoteProps {
  /** Seeds the draft at mount only — remount (key) to show another mark's note. */
  note: string;
  onNoteChange: (note: string) => void;
}

/**
 * Paste and drop are rejected: the popover portals outside the freeze shield,
 * and notes must be typed (anti-cheat, part-2 spec).
 */
export default function AnnotationNote({note, onNoteChange}: AnnotationNoteProps) {
  const [draft, setDraft] = useState(note);
  const saveDraft = useMemo(() => debounce(onNoteChange, NOTE_SAVE_DEBOUNCE_MS), [onNoteChange]);
  useEffect(() => () => saveDraft.flush(), [saveDraft]);

  return (
    <TextArea
      value={draft}
      aria-label='Add note'
      placeholder='Add note...'
      // Content-sized per design QA on #20493: 28ch wide, grows to 4 lines then scrolls.
      className='field-sizing-content max-h-[4lh] w-[28ch] resize-none'
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        setDraft(event.target.value);
        saveDraft(event.target.value);
      }}
      onBlur={() => saveDraft.flush()}
      onPaste={(event: React.ClipboardEvent) => event.preventDefault()}
      onDrop={(event: React.DragEvent) => event.preventDefault()}
    />
  );
}
